import { useMemo, useState } from "react";
import { formatMoney } from "@tagadapay/plugin-sdk";
import type { CheckoutData } from "@tagadapay/plugin-sdk/v2";
import { Skeleton } from "../ui/skeleton";

interface OrderSummaryProps {
  checkout: CheckoutData | undefined;
  pluginConfig: any;
  bundles: any[];
  isLoading: boolean;
  lineItems: any[];
  orderBumpsInCheckout: any[];
  preferLineDerivedTotal?: boolean;
}

const VARIANTS_MAP: Record<string, number> = {
  variant_66f7ce289152: 0,
  variant_011bc996e9e7: 1,
  variant_12bfc7469946: 2,
  variant_74ea93b23b15: 3,
};

const OrderSummaryItem = ({
  item,
  pluginConfig,
  bundles,
  ob_quant,
}: {
  item: any;
  pluginConfig: any;
  bundles: any[];
  ob_quant: number;
}) => {
  const {
    isBundle,
    isExpeditedShippingOrderBump,
    isLifetimeProtectionOrderBump,
    is90DayMoneyBackGuarantee,
    bundle,
  } = useMemo(() => {
    const isBundle = Object.values(pluginConfig.variants).includes(
      item.variantId,
    );
    const isExpeditedShippingOrderBump =
      pluginConfig.orderBumpIdExpeditedShippingVariant === item.variantId;
    const isLifetimeProtectionOrderBump =
      pluginConfig.orderBumpIdLifetimeProtectionVariant === item.variantId;
    const bundle = bundles.find((b) => b.variantId === item.variantId);

    const displayName =
      item.variant?.name.trim() || item.product?.name.trim() || "";

    const is90DayMoneyBackGuarantee = displayName
      .toLowerCase()
      .includes("90 day money-back guarantee");
    return {
      isBundle,
      isExpeditedShippingOrderBump,
      isLifetimeProtectionOrderBump,
      is90DayMoneyBackGuarantee,
      bundle,
    };
  }, [
    item.variantId,
    pluginConfig.variants,
    pluginConfig.orderBumpIdVylarasCareVariant,
    bundles,
  ]);

  const currency = bundle?.currency || pluginConfig.defaultCurrency;

  return (
    <div className="flex items-center justify-between gap-2 sm:gap-3">
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="relative shrink-0">
          {isBundle && (
            <img
              src={bundle?.images[0]}
              alt={bundle?.name || "Product"}
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-[60px] md:h-[60px] rounded border border-[rgb(225,225,225)]"
            />
          )}
          {is90DayMoneyBackGuarantee && (
            <img
              src="//assets.funnelkonnekt.com/c3586120-cca6-11ed-a5ff-e92bf37fd671/1687870531396_nooro90days.png"
              alt="90 Day Money-Back Guarantee"
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-[60px] md:h-[60px] rounded border border-[rgb(225,225,225)]"
            />
          )}
          {isExpeditedShippingOrderBump && (
            <img
              src={item.variant?.imageUrl || "//fnsh.imgix.net/16391/132009/1694055488-1688312587252_1_1_.png?auto=compress,format&fit=scale&w=96&h=87"}
              alt="Expedited Shipping"
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-[60px] md:h-[60px] rounded border border-[rgb(225,225,225)]"
            />
          )}
          {isLifetimeProtectionOrderBump && (
            <img
              src={item.variant?.imageUrl || "//fnsh.imgix.net/16391/132009/1694055494-1688313716977_1.jpg?auto=compress,format&fit=scale&w=96&h=91"}
              alt="Lifetime Protection"
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-[60px] md:h-[60px] rounded border border-[rgb(225,225,225)]"
            />
          )}
          <span className="absolute -right-1 sm:-right-2 -top-1 sm:-top-2 flex w-4 h-4 sm:w-5 sm:h-5 items-center justify-center rounded-full bg-gray-700 text-[10px] sm:text-xs text-white">
            1
          </span>
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-xs sm:text-[13px] md:text-[14px] wrap-break-word">
            {bundle?.name ||
              item.variant?.name.trim() ||
              item.product?.name.trim()}
          </span>
        </div>
      </div>
      {isBundle && (
        <div className="flex flex-col items-end text-left shrink-0">
          <p className="text-[11px] sm:text-xs md:text-[13px] font-bold text-[rgb(230,0,0)] line-through">
            Reg {formatMoney(bundle?.originalPrice || 0, String(currency))}
          </p>
          <p className="text-sm sm:text-base md:text-lg font-bold text-[rgb(102,185,102)]">50% OFF</p>
          <p className="text-sm sm:text-base md:text-lg font-bold">
            {formatMoney(Math.round(item.amount / (bundle?.quantity || 1)))}
          </p>
        </div>
      )}

      {isExpeditedShippingOrderBump && (
        <p className="text-xs sm:text-[13px] md:text-[14px] font-bold shrink-0">
          {formatMoney(Math.round(item.adjustedAmount || item.amount || 0))}
        </p>
      )}

      {isLifetimeProtectionOrderBump && (
        <p className="text-xs sm:text-[13px] md:text-[14px] font-bold shrink-0">
          {formatMoney(Math.round(item.adjustedAmount || item.amount || 0))}
        </p>
      )}

      {is90DayMoneyBackGuarantee && (
        <div className="flex flex-col items-end text-left shrink-0">
          <p className="text-[11px] sm:text-xs md:text-[13px] font-bold text-[rgb(230,0,0)] line-through">
            {formatMoney(item.amount || 0, String(currency))}
          </p>
          <p className="text-xs sm:text-sm">{formatMoney(Math.round(item.adjustedAmount))}</p>
        </div>
      )}
    </div>
  );
};

export function OrderSummary({
  checkout,
  pluginConfig,
  bundles,
  isLoading,
  lineItems,
  orderBumpsInCheckout,
  preferLineDerivedTotal = false,
}: OrderSummaryProps) {
  const [showOrderSummary, setShowOrderSummary] = useState(true);

  // Filter line items for calculation and display
  const filteredLineItems = useMemo(() => {
    if (!lineItems) return [];

    return lineItems
      .filter((item: any) => {
        const itemName = item.product?.name || item.variant?.name || "";
        return !itemName.includes("90 Day Money-Back Guarantee");
      })
      .filter((item: any) => {
        // Logic for Order Bumps - only include if selected
        const isLifetimeProtection =
          item.variantId === pluginConfig.orderBumpIdLifetimeProtectionVariant;
        const isExpeditedShipping =
          item.variantId === pluginConfig.orderBumpIdExpeditedShippingVariant;

        // If it's not an order bump, include it
        if (!isLifetimeProtection && !isExpeditedShipping) return true;

        // If it's an order bump, only include if selected
        const bump = orderBumpsInCheckout.find(
          (ob) => ob.variantId === item.variantId,
        );
        return bump?.selected;
      });
  }, [
    lineItems,
    pluginConfig.orderBumpIdLifetimeProtectionVariant,
    pluginConfig.orderBumpIdExpeditedShippingVariant,
    orderBumpsInCheckout,
  ]);

  // Calculate current variant ID for ob_quant logic
  const currentVariantId = useMemo(() => {
    return (
      Object.keys(VARIANTS_MAP).find((key) =>
        filteredLineItems.some((item: any) => item.variantId === key),
      ) || ""
    );
  }, [filteredLineItems]);

  const ob_quant = useMemo(() => {
    return (VARIANTS_MAP[currentVariantId] || 0) + 1;
  }, [currentVariantId]);

  const orderTotal = useMemo(() => {
    return filteredLineItems.reduce((acc: number, item: any) => {
      const currentAmount = item?.adjustedAmount || item?.amount || 0;
      return acc + currentAmount;
    }, 0);
  }, [filteredLineItems]);

  const summaryCurrency =
    checkout?.summary?.currency || pluginConfig.defaultCurrency;
  const summaryPromotionAmount = checkout?.summary?.totalPromotionAmount || 0;
  const summaryTotalAdjustedAmount = checkout?.summary?.totalAdjustedAmount;
  const displayOrderTotal = preferLineDerivedTotal
    ? orderTotal
    : typeof summaryTotalAdjustedAmount === "number"
      ? summaryTotalAdjustedAmount
      : orderTotal;

  const displayItems = useMemo(() => {
    if (!lineItems) return [];

    return lineItems
      .filter((item: any) => {
        // Show regular items (not order bumps) and selected order bumps
        const matchingOrderBump = orderBumpsInCheckout.find(
          (ob) => ob.variantId === item.variantId,
        );
        if (!matchingOrderBump) return true;
        return matchingOrderBump.selected;
      })
      .sort((a: any, b: any) => {
        const configVariantIds = Object.values(pluginConfig.variants);
        const aIndex = configVariantIds.indexOf(a.variantId);
        const bIndex = configVariantIds.indexOf(b.variantId);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });
  }, [lineItems, orderBumpsInCheckout, pluginConfig.variants]);

  if (isLoading) {
    return (
      <div>
        <div className="flex w-full items-center justify-between border-b border-t border-[rgba(175,175,175,0.34)] bg-[#fafafa] px-1.5 py-[15px]">
          <div className="flex items-center gap-2">
            <Skeleton className="size-5 rounded bg-[rgba(83,83,83,0.15)]" />
            <Skeleton className="h-4 w-32 bg-[rgba(83,83,83,0.15)]" />
          </div>
          <Skeleton className="h-4 w-20 bg-[rgba(83,83,83,0.15)]" />
        </div>
        <div className="space-y-3 p-3">
          {[1, 2, 3, 4].map((idx) => (
            <div className="flex items-center justify-between gap-3" key={idx}>
              <div className="flex items-center gap-3">
                <Skeleton className="size-10 rounded border border-[rgb(225,225,225)] bg-[rgba(102,185,102,0.1)]" />
                <Skeleton className="h-4 w-32 bg-[rgba(83,83,83,0.12)]" />
              </div>
              <Skeleton className="h-5 w-16 bg-[rgba(102,185,102,0.15)]" />
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-[rgb(232,232,234)] pt-2">
            <Skeleton className="h-4 w-12 bg-[rgba(83,83,83,0.15)]" />
            <Skeleton className="h-4 w-20 bg-[rgba(102,185,102,0.2)]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[rgba(175,175,175,0.34)] mt-3 sm:mt-4 md:mt-[15px] mx-0">
      <button
        type="button"
        onClick={() => setShowOrderSummary((v) => !v)}
        className="order-summary flex w-full items-center justify-between border-b border-t border-[rgba(175,175,175,0.34)] bg-[#fafafa] px-2 sm:px-3 md:px-1.5 py-3 sm:py-4 md:py-[15px] text-xs sm:text-[13px] md:text-[14px] font-bold text-[rgb(83,83,83)] font-montserrat"
      >
        <div className="flex items-center gap-1.5 sm:gap-2">
          <svg
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 sm:w-5 sm:h-5 shrink-0"
          >
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M17,18A2,2 0 0,1 19,20A2,2 0 0,1 17,22C15.89,22 15,21.1 15,20C15,18.89 15.89,18 17,18M1,2H4.27L5.21,4H20A1,1 0 0,1 21,5C21,5.17 20.95,5.34 20.88,5.5L17.3,11.97C16.96,12.58 16.3,13 15.55,13H8.1L7.2,14.63L7.17,14.75A0.25,0.25 0 0,0 7.42,15H19V17H7C5.89,17 5,16.1 5,15C5,14.65 5.09,14.32 5.24,14.04L6.6,11.59L3,4H1V2M7,18A2,2 0 0,1 9,20A2,2 0 0,1 7,22C5.89,22 5,21.1 5,20C5,18.89 5.89,18 7,18M16,11L18.78,6H6.14L8.5,11H16Z"
              fill="currentColor"
            ></path>
          </svg>
          <span className="select-none text-xs sm:text-sm md:text-[14px]">
            {showOrderSummary ? "Hide order summary" : "Show order summary"}
          </span>
          <svg
            className={`w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 shrink-0 transition-transform ${
              showOrderSummary ? "rotate-180" : ""
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 15 15"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.0581 4.55812L12.9419 5.44187L7.50001 10.8837L2.05814 5.44187L2.94189 4.55812L7.50001 9.11625L12.0581 4.55812Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div className="text-sm sm:text-[14px] md:text-[15px] shrink-0">
          {formatMoney(displayOrderTotal || 0, String(summaryCurrency))}
        </div>
      </button>

      {showOrderSummary && (
        <div className="space-y-2 sm:space-y-3 p-2 sm:p-2.5 md:p-[10px] bg-white">
          <div className="flex items-center justify-between text-xs sm:text-[13px] md:text-[14px] font-bold text-[rgb(83,83,83)]">
            <span className="text-[rgb(83,83,83)] font-montserrat font-bold">
              Product
            </span>
            <span className="text-[rgb(83,83,83)] font-montserrat font-bold">
              Price
            </span>
          </div>

          <div className="space-y-2 sm:space-y-3 text-[rgb(37,42,50)] font-montserrat text-xs sm:text-[13px] md:text-[14px]">
            {displayItems.map((item: any, idx: number) => (
              <OrderSummaryItem
                key={item.id || idx}
                item={item}
                pluginConfig={pluginConfig}
                bundles={bundles}
                ob_quant={ob_quant}
              />
            ))}
          </div>

          {summaryPromotionAmount > 0 && (
            <div className="flex items-center justify-between border-t border-[rgb(232,232,234)] pt-2 text-xs sm:text-[13px] md:text-[14px] font-bold text-[rgb(83,83,83)] font-montserrat">
              <span>Discount</span>
              <span className="text-[rgb(44,158,44)]">
                -{formatMoney(summaryPromotionAmount, String(summaryCurrency))}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-[rgb(232,232,234)] pt-2 text-xs sm:text-[13px] md:text-[14px] font-bold text-[rgb(83,83,83)] font-montserrat">
            <span>Total</span>
            <span>{formatMoney(displayOrderTotal || 0, String(summaryCurrency))}</span>
          </div>
        </div>
      )}
    </div>
  );
}
